import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentArrowDownIcon,
  HomeIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";
import api from "../utils/api";
import INRExportFormatsButton from "../components/INRExportFormatsButton";

function INRInvestorsFilterPage() {
  const [filters, setFilters] = useState({
    // Personal Information
    name: "", // Maps to name field
    phone_number: "",
    added_by: "",

    // Financial Information
    received: "",
    received_min: "",
    received_max: "",
    sent: "",
    sent_min: "",
    sent_max: "",
    balance: "",
    balance_min: "",
    balance_max: "",

    // Date and Other Information
    date: "",
    date_from: "",
    date_to: "",
    remarks: "",
  });
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Debug: Check authentication
    const token = localStorage.getItem("token");
    console.log("Auth token present:", !!token);
    console.log("Token length:", token?.length || 0);

    try {
      const params = Object.entries(filters)
        .filter(([_, v]) => v)
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join("&");

      console.log("Making INR filter request with params:", params);
      console.log("Full URL:", `/api/filter/inr-investors?${params}`);

      const res = await api.get(`/api/filter/inr-investors?${params}`);
      console.log("INR filter response:", res.data);
      setResults(res.data);
      setError("");
    } catch (err) {
      console.error("INR Filter Error:", err);
      console.error("Error response:", err.response);
      const errorMessage =
        err.response?.data?.message ||
        err.response?.statusText ||
        err.message ||
        "Filter failed - Unknown error";
      setError(
        `Error: ${errorMessage} (Status: ${err.response?.status || "Unknown"})`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      // Personal Information
      name: "",
      phone_number: "",
      added_by: "",

      // Financial Information
      received: "",
      received_min: "",
      received_max: "",
      sent: "",
      sent_min: "",
      sent_max: "",
      balance: "",
      balance_min: "",
      balance_max: "",

      // Date and Other Information
      date: "",
      date_from: "",
      date_to: "",
      remarks: "",
    });
    setResults([]);
    setError("");
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0.00";
    return `₹${parseFloat(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Enhanced Navigation Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/dashboard"
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                <HomeIcon className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Link>
              <Link
                to="/inr-investors"
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                <CurrencyDollarIcon className="h-4 w-4 mr-2" />
                INR Investors
              </Link>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <span>•</span>
                <span>Advanced INR Search & Filtering</span>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={clearFilters}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                Clear Filters
              </button>
              <div className="bg-green-50 px-3 py-1 rounded-full">
                <span className="text-sm font-medium text-green-700">
                  INR Advanced Search
                </span>
              </div>
            </div>
          </div>

          {/* Page Title and Description */}
          <div className="mt-4">
            <div className="flex items-center gap-3 mb-2">
              <FunnelIcon className="h-8 w-8 text-gray-700" />
              <h1 className="text-3xl font-bold text-gray-900">
                Advanced INR Investor Search
              </h1>
            </div>
            <p className="text-gray-600 max-w-4xl">
              Comprehensive search across all INR investor data fields including
              personal information, financial transactions, dates, and remarks.
              Filter by amount ranges, specific dates, and transaction details.
              Export filtered results in multiple formats.
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-sm">
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                💰 INR Transactions
              </span>
              <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full">
                🔍 Real-time Filtering
              </span>
              <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                📤 Multi-format Export
              </span>
              <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                💵 Amount Range Search
              </span>
            </div>
          </div>
        </div>

        {/* Filter Form */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center mb-4">
              <FunnelIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                Comprehensive INR Search Criteria
              </h3>
              <div className="ml-4 text-sm text-gray-500">
                Search across all INR investor data fields
              </div>
            </div>

            <form onSubmit={handleSearch} className="space-y-6">
              {/* Personal Information Section */}
              <div>
                <h4 className="text-md font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">
                  👤 Personal Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name/Description
                    </label>
                    <input
                      name="name"
                      placeholder="Enter name or description"
                      value={filters.name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      name="phone_number"
                      placeholder="Enter phone number"
                      value={filters.phone_number}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Added By
                    </label>
                    <input
                      name="added_by"
                      placeholder="Enter added by user"
                      value={filters.added_by}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Financial Information Section */}
              <div>
                <h4 className="text-md font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">
                  💰 Financial Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Received Amount (Exact)
                    </label>
                    <input
                      name="received"
                      type="number"
                      step="0.01"
                      placeholder="Enter exact received amount"
                      value={filters.received}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Received Range (Min - Max)
                    </label>
                    <div className="flex space-x-2">
                      <input
                        name="received_min"
                        type="number"
                        step="0.01"
                        placeholder="Min"
                        value={filters.received_min}
                        onChange={handleChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <input
                        name="received_max"
                        type="number"
                        step="0.01"
                        placeholder="Max"
                        value={filters.received_max}
                        onChange={handleChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sent Amount (Exact)
                    </label>
                    <input
                      name="sent"
                      type="number"
                      step="0.01"
                      placeholder="Enter exact sent amount"
                      value={filters.sent}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sent Range (Min - Max)
                    </label>
                    <div className="flex space-x-2">
                      <input
                        name="sent_min"
                        type="number"
                        step="0.01"
                        placeholder="Min"
                        value={filters.sent_min}
                        onChange={handleChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <input
                        name="sent_max"
                        type="number"
                        step="0.01"
                        placeholder="Max"
                        value={filters.sent_max}
                        onChange={handleChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Balance (Exact)
                    </label>
                    <input
                      name="balance"
                      type="number"
                      step="0.01"
                      placeholder="Enter exact balance"
                      value={filters.balance}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Balance Range (Min - Max)
                    </label>
                    <div className="flex space-x-2">
                      <input
                        name="balance_min"
                        type="number"
                        step="0.01"
                        placeholder="Min"
                        value={filters.balance_min}
                        onChange={handleChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <input
                        name="balance_max"
                        type="number"
                        step="0.01"
                        placeholder="Max"
                        value={filters.balance_max}
                        onChange={handleChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Date and Other Information Section */}
              <div>
                <h4 className="text-md font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">
                  📅 Date & Additional Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Specific Date
                    </label>
                    <input
                      name="date"
                      type="date"
                      value={filters.date}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date Range (From - To)
                    </label>
                    <div className="flex space-x-2">
                      <input
                        name="date_from"
                        type="date"
                        placeholder="From Date"
                        value={filters.date_from}
                        onChange={handleChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <input
                        name="date_to"
                        type="date"
                        placeholder="To Date"
                        value={filters.date_to}
                        onChange={handleChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Remarks
                    </label>
                    <input
                      name="remarks"
                      placeholder="Search in remarks"
                      value={filters.remarks}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={clearFilters}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Clear All
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                  {isLoading ? "Searching..." : "Search INR Records"}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Results Summary */}
        {results.length > 0 && (
          <div className="bg-white border-2 border-blue-100 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 p-6 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center mr-3">
                  <DocumentArrowDownIcon className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-lg font-semibold text-gray-900">
                    {results.length} INR Record{results.length !== 1 ? "s" : ""}{" "}
                    Found
                  </p>
                  <p className="text-sm text-gray-600">
                    Matching your search criteria
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    ✓ Search Complete
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    📊 {results.length} Records
                  </span>
                </div>

                <div className="flex items-center space-x-3 pl-3 border-l border-gray-200">
                  <div className="flex items-center space-x-2">
                    <svg
                      className="w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <span className="text-sm font-medium text-gray-700">
                      Export Results:
                    </span>
                  </div>
                  <div className="transform hover:scale-105 transition-transform duration-200">
                    <INRExportFormatsButton filters={filters} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Results Table */}
        {results.length > 0 && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name/Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Financial Summary
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Remarks
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Added By
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {results.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          #{record.id}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          📅{" "}
                          {record.date
                            ? new Date(record.date).toLocaleDateString()
                            : "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {record.name || "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {record.phone_number ? (
                            <div className="flex items-center space-x-2">
                              <span>📱 {record.phone_number}</span>
                              <button
                                onClick={() =>
                                  copyToClipboard(record.phone_number)
                                }
                                className="text-blue-600 hover:text-blue-900 text-xs"
                              >
                                Copy
                              </button>
                            </div>
                          ) : (
                            <span className="text-gray-400">No Phone</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm space-y-1">
                          {record.received && (
                            <div className="text-green-600 font-medium">
                              ↓ Received: {formatCurrency(record.received)}
                            </div>
                          )}
                          {record.sent && (
                            <div className="text-red-600 font-medium">
                              ↑ Sent: {formatCurrency(record.sent)}
                            </div>
                          )}
                          {record.balance && (
                            <div className="text-blue-600 font-medium">
                              💰 Balance: {formatCurrency(record.balance)}
                            </div>
                          )}
                          {!record.received &&
                            !record.sent &&
                            !record.balance && (
                              <span className="text-gray-400">
                                No Financial Data
                              </span>
                            )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {record.remarks ? (
                            <div
                              className="max-w-xs overflow-hidden"
                              title={record.remarks}
                            >
                              💬{" "}
                              {record.remarks.length > 50
                                ? record.remarks.substring(0, 50) + "..."
                                : record.remarks}
                            </div>
                          ) : (
                            <span className="text-gray-400">No Remarks</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          👤 {record.added_by || "Unknown"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {record.created_at
                            ? new Date(record.created_at).toLocaleDateString()
                            : ""}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No Results Message */}
        {results.length === 0 && error === "" && (
          <div className="bg-white shadow rounded-lg">
            <div className="text-center py-12">
              <FunnelIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No search results
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Try adjusting your search criteria or use the search button to
                find INR investor records.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default INRInvestorsFilterPage;
